// ============================================================
// ColonieAdminReport.js
// Admin report: all registrations with package breakdown,
// printable report + CSV export, inline pay button
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, BarChart2, Search, RefreshCw, FileText, DollarSign, Loader, Printer } from 'lucide-react';
import { PKG_LABEL, PKG_SHORT } from './ColoniePrintUtils';

const fmt = v => `$${parseFloat(v || 0).toFixed(2)}`;

const ColonieAdminReport = ({ onClose, apiCall, showError, setPay }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [psFilter, setPs]     = useState('All');
  const [pkgFilter, setPkg]   = useState('All');
  const [esFilter, setEs]     = useState('All');
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (psFilter  !== 'All') params.set('paymentStatus',  psFilter);
      if (pkgFilter !== 'All') params.set('packageType',    pkgFilter);
      if (esFilter  !== 'All') params.set('externalStatus', esFilter);
      setData(await apiCall(`/colonie/reports/full?${params}`));
    } catch (e) { showError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psFilter, pkgFilter, esFilter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [psFilter, pkgFilter, esFilter]);

  const rows = useMemo(() => {
    if (!data?.registrations) return [];
    if (!search) return data.registrations;
    const s = search.toLowerCase();
    return data.registrations.filter(r =>
      r.ChildName?.toLowerCase().includes(s) ||
      r.MotherName?.toLowerCase().includes(s) ||
      r.FatherName?.toLowerCase().includes(s) ||
      r.MotherPhone?.includes(s) ||
      (r.FamilyID && String(r.FamilyID).includes(s))
    );
  }, [data, search]);

  // ── CSV Export ────────────────────────────────────────────
  const exportCSV = () => {
    if (!rows.length) return;
    const headers = [
      'Child Name','Mother','Father','Phone','Class','Branch','Family ID',
      'Package','Months','Transport','Late Pickup','Nerd',
      'Total Charged','Total Paid','Balance','Payment Status',
      'Portal Status','Last Payment','Payment Count',
    ];
    const csvRows = rows.map(r => [
      r.ChildName, r.MotherName||'', r.FatherName||'', r.MotherPhone||'',
      r.Class||'', r.Branch||'', r.FamilyID||'',
      PKG_SHORT[r.PackageType] || r.PackageType || 'Not enrolled',
      r.MonthsCharged || '',
      r.HasTransport ? `$${parseFloat(r.TransportFeeAmt||0).toFixed(2)}` : '',
      r.LatePickup ? (r.LatePickup==='3to330'?'3–3:30':'3–4:00') : '',
      r.HasNerd ? (r.NerdType==='outcamp'?'Outside':'In camp') : '',
      r.TotalCharged!=null ? `$${parseFloat(r.TotalCharged).toFixed(2)}` : '',
      r.TotalPaid!=null    ? `$${parseFloat(r.TotalPaid).toFixed(2)}`    : '',
      r.BalanceRemaining!=null ? `$${parseFloat(r.BalanceRemaining).toFixed(2)}` : '',
      r.PaymentStatus || 'Not enrolled',
      r.ExternalStatus || '',
      r.LastPaymentDate ? new Date(r.LastPaymentDate).toLocaleDateString('en-GB') : '',
      r.PaymentCount || 0,
      r.Discount ? `$${parseFloat(r.Discount).toFixed(2)}` : '',
      r.DiscountNotes || '',
    ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(','));

    const csv  = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `colonie_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print Report ──────────────────────────────────────────
  const handlePrint = () => {
    if (!rows.length) return;
    const s = data?.summary;
    const today = new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'long', year:'numeric' });

    const totalCharged    = rows.reduce((sum, r) => sum + parseFloat(r.TotalCharged    || 0), 0);
    const totalPaid       = rows.reduce((sum, r) => sum + parseFloat(r.TotalPaid       || 0), 0);
    const totalOutstanding = rows.reduce((sum, r) => sum + parseFloat(r.BalanceRemaining || 0), 0);

    // Package breakdown
    const pkgBreak = {};
    rows.filter(r => r.PackageType).forEach(r => {
      const k = PKG_LABEL[r.PackageType] || r.PackageType;
      if (!pkgBreak[k]) pkgBreak[k] = { count: 0, charged: 0, paid: 0 };
      pkgBreak[k].count++;
      pkgBreak[k].charged += parseFloat(r.TotalCharged || 0);
      pkgBreak[k].paid    += parseFloat(r.TotalPaid    || 0);
    });

    const pkgSummaryHTML = `
      <table style="width:100%;border-collapse:collapse;margin-bottom:8px;">
        <thead>
          <tr style="background-color:#f3f4f6;border-bottom:2px solid #9ca3af;">
            <th style="padding:4px 6px;text-align:left;font-size:9px;font-weight:600;">Package</th>
            <th style="padding:4px 6px;text-align:right;font-size:9px;font-weight:600;">Children</th>
            <th style="padding:4px 6px;text-align:right;font-size:9px;font-weight:600;">Total Charged</th>
            <th style="padding:4px 6px;text-align:right;font-size:9px;font-weight:600;">Total Paid</th>
            <th style="padding:4px 6px;text-align:right;font-size:9px;font-weight:600;">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          ${Object.entries(pkgBreak).map(([pkg, d]) => `
            <tr style="border-bottom:1px solid #e5e7eb;">
              <td style="padding:3px 6px;font-size:9px;font-weight:600;">${pkg}</td>
              <td style="padding:3px 6px;font-size:9px;text-align:right;">${d.count}</td>
              <td style="padding:3px 6px;font-size:9px;text-align:right;">$${d.charged.toFixed(2)}</td>
              <td style="padding:3px 6px;font-size:9px;text-align:right;color:#16a34a;">$${d.paid.toFixed(2)}</td>
              <td style="padding:3px 6px;font-size:9px;text-align:right;color:${(d.charged-d.paid)>0?'#dc2626':'#16a34a'};">$${(d.charged-d.paid).toFixed(2)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`;

    const mainTableHTML = `
      <table style="width:100%;border-collapse:collapse;font-size:8.5px;">
        <thead>
          <tr style="background-color:#fff7ed;border-bottom:2px solid #f97316;">
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:13%">Child</th>
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:14%">Mother / Phone</th>
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:10%">Class</th>
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:15%">Package</th>
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:12%">Add-ons</th>
            <th style="padding:3px 5px;text-align:right;font-size:8px;font-weight:600;color:#374151;width:10%">Charged</th>
            <th style="padding:3px 5px;text-align:right;font-size:8px;font-weight:600;color:#374151;width:10%">Paid</th>
            <th style="padding:3px 5px;text-align:right;font-size:8px;font-weight:600;color:#374151;width:10%">Balance</th>
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:8%">Status</th>
            <th style="padding:3px 5px;text-align:left;font-size:8px;font-weight:600;color:#374151;width:8%">Portal</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map((r, idx) => {
            const bal = parseFloat(r.BalanceRemaining || 0);
            const disc = parseFloat(r.Discount || 0);
            const addons = [];
            if (r.HasTransport) addons.push(`Bus $${parseFloat(r.TransportFeeAmt||0).toFixed(0)}`);
            if (r.LatePickup)   addons.push(r.LatePickup==='3to330'?'Late 3:30':'Late 4:00');
            if (r.HasNerd)      addons.push(r.NerdType==='outcamp'?'Nerd out':'Nerd in');
            return `
              <tr style="border-bottom:1px solid #f0f0f0;background-color:${idx%2===0?'#fff':'#fafafa'};">
                <td style="padding:3px 4px;font-size:8.5px;font-weight:600;color:#111;">${r.ChildName}${r.FamilyID?`<br><span style="font-size:7px;color:#2563eb;">${r.FamilyID}</span>`:''}</td>
                <td style="padding:3px 4px;font-size:8px;color:#555;">${r.MotherName||''}<br><span style="font-size:7.5px;">${r.MotherPhone||''}</span></td>
                <td style="padding:3px 4px;font-size:8.5px;color:#333;">${r.Class||'—'}</td>
                <td style="padding:3px 4px;">
                  ${r.PackageType
                    ? `<span style="background:#dbeafe;color:#1e40af;padding:1px 4px;border-radius:3px;font-size:8px;font-weight:600;">${PKG_SHORT[r.PackageType]||r.PackageType}</span>${r.PackageType==='Monthly3Days'?`<span style="font-size:7.5px;color:#666;"> ${r.MonthsCharged||1}mo</span>`:''}`
                    : '<span style="color:#ccc;">—</span>'}
                  ${disc > 0 ? `<br><span style="font-size:7.5px;color:#16a34a;">-$${disc.toFixed(2)} disc (${r.DiscountNotes||''})</span>` : ''}
                </td>
                <td style="padding:3px 4px;color:#ea580c;font-size:8px;">${addons.join('<br>')||'—'}</td>
                <td style="padding:3px 4px;font-size:8.5px;text-align:right;color:#333;">${r.TotalCharged!=null?`$${parseFloat(r.TotalCharged).toFixed(2)}`:'—'}</td>
                <td style="padding:3px 4px;font-size:8.5px;text-align:right;font-weight:600;color:#16a34a;">${r.TotalPaid!=null?`$${parseFloat(r.TotalPaid).toFixed(2)}`:'—'}</td>
                <td style="padding:3px 4px;font-size:8.5px;text-align:right;font-weight:600;color:${bal>0?'#dc2626':'#16a34a'};">${r.BalanceRemaining!=null?`$${bal.toFixed(2)}`:'—'}</td>
                <td style="padding:3px 4px;font-size:8px;color:${r.PaymentStatus==='Paid'?'#16a34a':r.PaymentStatus==='Partial'?'#ea580c':'#dc2626'};">${r.PaymentStatus||'Not enrolled'}</td>
                <td style="padding:3px 4px;font-size:8px;color:${r.ExternalStatus==='Confirmed'?'#7c3aed':'#b45309'};">${r.ExternalStatus||''}</td>
              </tr>`;
          }).join('')}
        </tbody>
        <tfoot>
          <tr style="background-color:#fff7ed;border-top:2px solid #f97316;font-weight:700;">
            <td colspan="5" style="padding:4px;text-align:right;font-size:9px;">${rows.length} children total</td>
            <td style="padding:4px;text-align:right;font-size:9px;">$${totalCharged.toFixed(2)}</td>
            <td style="padding:4px;text-align:right;font-size:9px;color:#16a34a;">$${totalPaid.toFixed(2)}</td>
            <td style="padding:4px;text-align:right;font-size:9px;color:#dc2626;">$${totalOutstanding.toFixed(2)}</td>
            <td colspan="2"></td>
          </tr>
        </tfoot>
      </table>`;

    const summaryBoxHTML = `
      <div style="margin-top:24px;padding:12px;background-color:#f3f4f6;border:2px solid #9ca3af;border-radius:6px;margin-top:12px;">
        <h3 style="font-size:12px;font-weight:700;text-align:center;margin:0 0 10px 0;color:#111827;">Summary</h3>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Total Registered:</span><span style="font-weight:700;">${s?.TotalRegistrations||rows.length}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Enrolled:</span><span style="font-weight:700;">${s?.TotalEnrolled||''}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Fully Paid:</span><span style="font-weight:700;color:#16a34a;">${s?.FullyPaid||''}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Partial:</span><span style="font-weight:700;color:#ea580c;">${s?.PartiallyPaid||''}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Unpaid / Not enrolled:</span><span style="font-weight:700;color:#dc2626;">${s?.Unpaid||''}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Confirmed on Portal:</span><span style="font-weight:700;color:#7c3aed;">${s?.ConfirmedOnPortal||''}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Total Charged:</span><span style="font-weight:700;">$${totalCharged.toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:10px;padding:3px 0;border-bottom:1px solid #d1d5db;">
          <span style="color:#4b5563;">Total Collected:</span><span style="font-weight:700;color:#16a34a;">$${totalPaid.toFixed(2)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;font-size:12px;font-weight:700;padding:6px 0 0 0;margin-top:4px;border-top:2px solid #6b7280;">
          <span style="color:${totalOutstanding>0?'#dc2626':'#16a34a'};">Outstanding Balance:</span>
          <span style="color:${totalOutstanding>0?'#dc2626':'#16a34a'};">$${totalOutstanding.toFixed(2)}</span>
        </div>
      </div>`;

    const windowPrint = window.open('', '', 'width=1200,height=850');
    windowPrint.document.write(`
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Colonie Admin Report</title>
          <style>
            @page { size: A4 landscape; margin: 8mm 10mm; }
            * { box-sizing: border-box; }
            html, body { width: 277mm; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; color: #1f2937; font-size: 9px; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div style="text-align:center;margin-bottom:14px;">
            <h1 style="font-size:16px;margin:0 0 4px 0;color:#111827;">Collège des Apôtres</h1>
            <h2 style="font-size:12px;margin:0 0 3px 0;color:#f97316;">Colonie Des Rêves — Admin Report</h2>
            <p style="font-size:9px;color:#6b7280;margin:0;">Generated: ${today} &nbsp;|&nbsp; Filters: Payment=${psFilter}, Package=${pkgFilter}, Portal=${esFilter}</p>
          </div>

          <div style="margin-bottom:14px;">
            <h3 style="font-size:11px;font-weight:700;margin:0 0 6px 0;background-color:#dbeafe;padding:5px 10px;border-radius:4px;color:#1e3a8a;">
              Package Summary
            </h3>
            ${pkgSummaryHTML}
          </div>

          <div style="margin-bottom:14px;">
            <h3 style="font-size:11px;font-weight:700;margin:0 0 6px 0;background-color:#fff7ed;padding:5px 10px;border-radius:4px;color:#9a3412;">
              Full Registration List (${rows.length} children)
            </h3>
            ${mainTableHTML}
          </div>

          ${summaryBoxHTML}
        </body>
      </html>
    `);
    windowPrint.document.close();
    windowPrint.focus();
    windowPrint.print();
    windowPrint.close();
  };

  const s = data?.summary;

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:50, padding:'1rem', overflowY:'auto' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-4 flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-blue-800 to-indigo-800 rounded-t-2xl shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <BarChart2 size={20}/>Admin Report — Colonie Des Rêves
            </h2>
            <p className="text-blue-200 text-sm mt-0.5">Full registration breakdown · Summer 2026</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5"><X size={18}/></button>
        </div>

        {/* Summary cards */}
        {s && (
          <div className="grid grid-cols-3 md:grid-cols-9 gap-3 p-5 pb-0">
            {[
              ['Registered',  s.TotalRegistrations, 'text-gray-700'],
              ['Enrolled',    s.TotalEnrolled,       'text-blue-700'],
              ['Fully Paid',  s.FullyPaid,           'text-green-700'],
              ['Partial',     s.PartiallyPaid,       'text-orange-600'],
              ['Unpaid',      s.Unpaid,              'text-red-600'],
              ['Portal OK',   s.ConfirmedOnPortal,   'text-purple-700'],
              ['Charged',     fmt(s.TotalCharged),   'text-gray-700'],
              ['Collected',   fmt(s.TotalPaid),      'text-green-700'],
              ['Outstanding', fmt(s.TotalOutstanding), s.TotalOutstanding > 0 ? 'text-red-600' : 'text-green-700'],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-gray-50 border border-gray-100 rounded-xl p-2.5 text-center">
                <div className={`text-base font-bold ${c}`}>{v}</div>
                <div className="text-xs text-gray-400 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters + actions */}
        <div className="flex items-center gap-3 p-5 pb-3 flex-wrap">
          <div className="relative flex-1 min-w-40">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name, phone…"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-400"/>
          </div>
          {[
            { label:'Payment', val:psFilter,  set:setPs,  opts:['All','Unpaid','Partial','Paid'] },
            { label:'Package', val:pkgFilter, set:setPkg, opts:['All','Cotisation','Season3Days','Monthly3Days','NerdOnly','Trial'] },
            { label:'Portal',  val:esFilter,  set:setEs,  opts:['All','Pending','Confirmed'] },
          ].map(f => (
            <select key={f.label} value={f.val} onChange={e => f.set(e.target.value)}
              className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400">
              {f.opts.map(o => <option key={o} value={o}>{f.label}: {o}</option>)}
            </select>
          ))}
          <button onClick={handlePrint} disabled={!rows.length}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
            <Printer size={14}/>Print Report
          </button>
          <button onClick={exportCSV} disabled={!rows.length}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
            <FileText size={14}/>Export CSV
          </button>
          <button onClick={load} className="px-3 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700">
            <RefreshCw size={14}/>
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-5 pt-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader size={26} className="animate-spin text-blue-600"/></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No registrations match the selected filters.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-blue-50 text-blue-900 uppercase">
                  {['Child','Parents / Phone','Class','Package','Add-ons','Charged','Paid','Balance','Status','Portal','Action'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map(r => {
                  const bal      = parseFloat(r.BalanceRemaining || 0);
                  const hasEnroll = !!r.EnrollmentID;
                  const addons   = [];
                  if (r.HasTransport) addons.push(`Bus $${parseFloat(r.TransportFeeAmt||0).toFixed(0)}`);
                  if (r.LatePickup)   addons.push(r.LatePickup==='3to330'?'Late 3:30':'Late 4:00');
                  if (r.HasNerd)      addons.push(r.NerdType==='outcamp'?'Nerd out':'Nerd in');

                  return (
                    <tr key={r.RegistrationID} className="hover:bg-blue-50/30">
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-gray-800">{r.ChildName}</div>
                        {r.FamilyID && <div className="text-blue-600">{r.FamilyID}</div>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-gray-700">{r.MotherName}</div>
                        <div className="text-gray-400">{r.FatherName}</div>
                        <div className="text-gray-500">{r.MotherPhone}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-gray-700">{r.Class}</div>
                        <div className="text-gray-400">{r.Branch}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        {hasEnroll ? (
                          <div>
                            <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-semibold">
                              {PKG_SHORT[r.PackageType] || r.PackageType}
                            </span>
                            {r.PackageType==='Monthly3Days' && (
                              <div className="text-gray-400 mt-0.5">{r.MonthsCharged||1}mo</div>
                            )}
                            {parseFloat(r.Discount||0) > 0 && (
                              <div className="text-emerald-600 font-semibold text-xs mt-0.5">
                                -{fmt(r.Discount)} disc.
                                {r.DiscountNotes && <span className="text-gray-400 font-normal ml-1">({r.DiscountNotes})</span>}
                              </div>
                            )}
                          </div>
                        ) : <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {addons.length > 0
                          ? <div className="space-y-0.5">{addons.map((a,i) => <div key={i} className="text-orange-600 font-medium">{a}</div>)}</div>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-gray-700">{hasEnroll ? fmt(r.TotalCharged) : '—'}</td>
                      <td className="px-3 py-2.5 font-semibold text-green-700">{hasEnroll ? fmt(r.TotalPaid) : '—'}</td>
                      <td className={`px-3 py-2.5 font-bold ${bal>0?'text-red-600':hasEnroll?'text-green-600':'text-gray-300'}`}>
                        {hasEnroll ? fmt(bal) : '—'}
                      </td>
                      <td className="px-3 py-2.5">
                        {hasEnroll
                          ? <span className={`px-2 py-0.5 rounded-full font-semibold border ${
                              r.PaymentStatus==='Paid'    ?'bg-green-100 text-green-700 border-green-300':
                              r.PaymentStatus==='Partial' ?'bg-orange-100 text-orange-700 border-orange-300':
                                                           'bg-red-100 text-red-700 border-red-300'
                            }`}>{r.PaymentStatus||'Unpaid'}</span>
                          : <span className="text-gray-300 italic">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full font-semibold border ${
                          r.ExternalStatus==='Confirmed'?'bg-purple-100 text-purple-700 border-purple-300':'bg-yellow-100 text-yellow-700 border-yellow-300'
                        }`}>{r.ExternalStatus}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        {hasEnroll && bal > 0 && (
                          <button onClick={() => { onClose(); setPay(r); }}
                            className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-lg font-semibold">
                            <DollarSign size={11}/>Pay
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-semibold text-sm">
                  <td colSpan={5} className="px-3 py-2.5 text-gray-600">{rows.length} children</td>
                  <td className="px-3 py-2.5 text-gray-800">{fmt(rows.reduce((s,r)=>s+parseFloat(r.TotalCharged||0),0))}</td>
                  <td className="px-3 py-2.5 text-green-700">{fmt(rows.reduce((s,r)=>s+parseFloat(r.TotalPaid||0),0))}</td>
                  <td className="px-3 py-2.5 text-red-600">{fmt(rows.reduce((s,r)=>s+parseFloat(r.BalanceRemaining||0),0))}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ColonieAdminReport;
